import sha256 from 'crypto-js/sha256';
import { cloneDeep, isEmpty } from 'lodash';
import { message } from 'antd';

import api from '@/api';
import { api as apiv2 } from '@/api/api.v2';
import { NODE_TYPE } from '@/lib/nodeType';
import { putDataIntoLocalStorage, getDataFromLocalStorage } from '@/lib/localStorage';
import storeRematch from '@/store';

import {
    getItemByKeyRecursive,
    removeItemByKeyRecursive,
    setItemByKeyRecursive,
    prepareDataRecursive,
    setUniqueKeyToItemsRecursive,
} from '@/components/Lists/lib';

export const itemInLists = currentItem => {
    if (currentItem) {
        const rootState = storeRematch.getState();
        const { data } = rootState.lists;
        return (data ?? []).find(item => item?.id === currentItem.id);
    }
    return false;
};

const findObjectById = (id, objects) => {
    for (const obj of objects) {
        if (obj.id === id) {
            return obj;
        }

        if (obj.children && Array.isArray(obj.children) && obj.children.length > 0) {
            const found = findObjectById(id, obj.children);
            if (found) {
                return found;
            }
        }
    }

    return null;
};

const filterWell = payload => {
    return payload.map(item => {
        const { gisData, ...rest } = item;
        return rest.children?.length ? { ...rest, children: filterWell(rest.children) } : rest;
    });
};

const LISTS_DATA = 'listsData';

const listsModel = {
    state: {
        data: null,
        treeData: [],
        treeDataLoading: false,
        extSearchLoading: false,
        extSearchData: [],
        extSearchText: '',
        extSearchGroupKey: null,
        extSearchSelectedRows: [],
        activeTreeNode: null,
        activeItemKey: null,
        activeGroupKey: null,
        isShowSearch: false,
        isExtShowSearch: false,
        matchesCount: null,
        totalMatchesCount: null,
        visibilityID: false,
        flaggedItemKeys: [], // Добавить key выделенных элементов для метки их флагом
        error: null,

        /* Параметр отвечает за видимость модального окна, в котором реализуется функционал изменений порядка скважин в группе.
        Расположен здесь по причине вложенности элементов, вызывающих модальное окно */
        isMovementVisible: false,
        /* Параметр хранит информацию о ключе группы. Эта ключ нужен для того, чтобы получить все эелменты
        группы и дать возможность изменения порядке этих элементов */
        groupKeyForReorder: null,
    },
    reducers: {
        'tree/setCalculationObject': (state, payload) => {
            if (payload?.id) {
                return { ...state, activeItemKey: null, activeGroupKey: null };
            }
            return state;
        },
        'setTreeData'(state, payload) {
            return { ...state, treeData: payload };
        },
        'setTreeDataLoading'(state, payload) {
            return { ...state, treeDataLoading: payload };
        },
        'setExtSearchLoading'(state, payload) {
            return { ...state, extSearchLoading: payload };
        },
        'setExtSearchData'(state, payload) {
            return {
                ...state,
                extSearchData: payload.data,
                matchesCount: payload.matchesCount,
                totalMatchesCount: payload.totalMatchesCount,
            };
        },
        'setExtSearchText'(state, payload) {
            return { ...state, extSearchText: payload };
        },
        'setExtSearchGroupKey'(state, payload) {
            return { ...state, extSearchGroupKey: payload };
        },
        'setExtSearchSelectedRows'(state, payload) {
            return { ...state, extSearchSelectedRows: payload };
        },
        'setData'(state, payload) {
            return { ...state, data: payload };
        },
        'setActiveTreeNode'(state, payload) {
            return { ...state, activeTreeNode: payload };
        },
        'setActiveItemKey'(state, payload) {
            return { ...state, activeItemKey: payload };
        },
        'setActiveGroupKey'(state, payload) {
            return { ...state, activeGroupKey: payload };
        },
        'setIsShowSearch'(state, payload) {
            return { ...state, isShowSearch: payload };
        },
        'setIsExtShowSearch'(state, payload) {
            return { ...state, isExtShowSearch: payload };
        },
        'setFlaggedItemKeys'(state, payload) {
            return { ...state, flaggedItemKeys: payload };
        },
        'setError'(state, payload) {
            return { ...state, error: payload };
        },
        'clearError'(state) {
            return { ...state, error: null };
        },
        'setVisibilityID'(state, payload) {
            return { ...state, visibilityID: payload };
        },
        'setGroupKeyForReorder'(state, payload) {
            return {
                ...state,
                isMovementVisible: true,
                groupKeyForReorder: payload,
            };
        },
        'setMovementVisible'(state, payload) {
            return {
                ...state,
                isMovementVisible: payload,
            };
        },
        'setNewOrder'(state, payload) {
            const sortedGroupItemByKey = state.data.map(item => {
                if (item.key === state.groupKeyForReorder) {
                    return { ...item, children: payload };
                }
                return item;
            });

            return { ...state, data: sortedGroupItemByKey };
        },
    },
    effects: dispatch => ({
        async fetchTreeData(payload, rootState) {
            if (rootState.lists.treeData.length == 0) {
                dispatch.lists.setTreeDataLoading(true);
                try {
                    const data = await api.tree.fetchTreeData();
                    dispatch.lists.setTreeData(data.fields);
                } catch (error) {
                    message.error('Не удалось получить список месторождений');
                    console.error(error);
                } finally {
                    dispatch.lists.setTreeDataLoading(false);
                }
            }
        },
        async fetchExtSearchData(payload, rootState) {
            dispatch.lists.setExtSearchLoading(true);
            try {
                // JSON.parse() используется для того, чтобы поле value в TreeSelect (antd) оставалось уникальной строкой.
                // На бэке все параметры опциональны, кроме searchList
                // если не выбрано месторождение, парсим пустой объект, чтобы не падало в ошибку
                const { fieldId, areaId, elevationId, organizationId } = JSON.parse(
                    rootState.lists.activeTreeNode ?? '{}',
                );
                const body = {
                    searchList: payload.searchList,
                    fieldId: fieldId,
                    areaId: areaId,
                    elevationId: elevationId,
                    organizationId: organizationId,
                };
                const data = await api.tree.fetchExtSearch(body);

                dispatch.lists.setExtSearchData({
                    data: data.data.map((e, ind) => {
                        return { ...e, index: ind + 1 };
                    }),
                    matchesCount: data.matches_count,
                    totalMatchesCount: data.total_matches_count,
                });
                dispatch.lists.setExtSearchSelectedRows([]);
            } catch (error) {
                message.error('Не удалось выполнить поиск');
                console.error(error);
            } finally {
                dispatch.lists.setExtSearchLoading(false);
            }
        },

        async fetchExtSearchDataById(payload, rootState) {
            dispatch(this.setExtSearchLoading(true));
            try {
                const res = await api.tree.fetchExtSearchById(payload.searchList);
                const dataWithIndex = res.data.map((e, ind) => ({ ...e, index: ind + 1 }));

                dispatch(this.setExtSearchData(dataWithIndex));
                dispatch(this.setExtSearchSelectedRows([]));

                const uwiText = dataWithIndex.map(e => e.UWI).join('\n');
                dispatch(this.setExtSearchText(uwiText));
            } catch (err) {
                message.error('Не удалось выполнить поиск');
                console.error(err);
            } finally {
                dispatch(this.setExtSearchLoading(false));
            }
        },

        async uploadFileEffect(payload) {
            const { text } = payload;
            dispatch.lists.setExtSearchText(text);
            dispatch.lists.fetchExtSearchData({
                searchList: payload.text
                    .replaceAll('\r', '')
                    .split('\n')
                    .filter(Boolean)
                    .map(e => e.trim()),
            });
        },

        async openGroupEffect(payload, rootState) {
            const { ids, groupKey } = payload;
            dispatch.lists.setExtSearchGroupKey(groupKey);

            await dispatch.lists.fetchExtSearchDataById({
                searchList: ids,
            });
        },

        // можно использовать в других местах
        async getListDataFromDB() {
            try {
                const { data } = getDataFromLocalStorage({ key: LISTS_DATA }) ?? {};

                return data;
            } catch (error) {
                console.error("Can't get attr. data from localStorage: ", error);
                throw new Error(String(error));
            }
        },
        // можно использовать в других местах
        async putListDataIntoDB({ data }) {
            try {
                const response = putDataIntoLocalStorage({ key: LISTS_DATA, data });
                return response;
            } catch (error) {
                console.error("Can't put data to localStorage: ", error);
                throw new Error(String(error));
            }
        },
        async addCustomMetadata(payload) {
            const item = cloneDeep(payload);

            if (!Object.keys(item).includes('columns')) {
                const columns = await dispatch.settings.loadColumns();
                Object.assign(item, { columns });
            }
            //TODO find out where it uses
            // if (!Object.keys(item).includes('gisData')) {
            //     const gisData = await dispatch.settings.loadGisData();
            //     Object.assign(item, { gisData });
            // }

            return item;
        },
        async loadData() {
            try {
                const dbData = await dispatch.lists.getListDataFromDB();

                if (dbData) {
                    dispatch.lists.setDataWithDisplaySettings(dbData);
                }

                return true;
            } catch (error) {
                console.error("Can't load data from localStorage: ", error);
                throw new Error(String(error));
            }
        },
        async saveData(payload) {
            try {
                const data = payload ? filterWell(payload) : payload;
                const response = await dispatch.lists.putListDataIntoDB({ data });

                return response;
            } catch (error) {
                console.error("Can't save data from localStorage: ", error);
                throw new Error(String(error));
            }
        },
        async selectItem(payload, rootState) {
            // TODO: придумать как-то лучше, так как в момент вызова они могут быть ещё пустыми

            dispatch.lists.setActiveItemKey(payload);
            // dispatch.wellResearchFilterReport.resetState();

            const activeItem = getItemByKeyRecursive(rootState.lists.data, payload);
            const newData = cloneDeep(rootState.lists.data);

            if (!activeItem?.fieldId && activeItem?.id) {
                const wellInfo = await dispatch.lists.fetchWellsInfo([activeItem?.id]);
                activeItem.fieldId = wellInfo[0]?.fieldId;
            }
            const updateData = async item => {
                if (!item) return;
                const itemKeys = Object.keys(item);
                if (!itemKeys.includes('gisData') || !itemKeys.includes('columns')) {
                    const updatedItem = await dispatch.lists.addCustomMetadata(item);
                    const updatedNewData = setItemByKeyRecursive(newData, item.key, updatedItem);
                    dispatch.lists.setDataWithDisplaySettings(updatedNewData);
                    await dispatch.lists.saveData(updatedNewData);
                }
            };

            if (activeItem?.children) {
                for (const item of activeItem.children) {
                    await updateData(item);
                }
            } else {
                await updateData(activeItem);
            }

            if (
                !rootState.curveSettings.linkingMode &&
                (!isEmpty(rootState.tabletObjectsModel.object) || !isEmpty(rootState.tabletObjectsModel.multiObject))
            ) {
                dispatch.tabletObjectsModel.resetTabletActiveObject();
            }

            if (activeItem && activeItem?.parentKey) {
                // Проверяем, что родительская группа действительно содержит выбранный элемент
                const parentGroup = getItemByKeyRecursive(rootState.lists.data, activeItem.parentKey);
                if (
                    parentGroup &&
                    parentGroup.children &&
                    parentGroup.children.some(child => child.key === activeItem.key)
                ) {
                    dispatch.lists.setActiveGroupKey(activeItem.parentKey);
                    dispatch.tablets.toggleItemGroupIsCheck(true);
                } else {
                    // Если родительская группа не содержит выбранный элемент, сбрасываем parentKey
                    const updatedItem = { ...activeItem };
                    delete updatedItem.parentKey;
                    const updatedNewData = setItemByKeyRecursive(newData, activeItem.key, updatedItem);
                    dispatch.lists.setDataWithDisplaySettings(updatedNewData);
                    await dispatch.lists.saveData(updatedNewData);

                    dispatch.lists.setActiveGroupKey(null);
                    dispatch.tablets.toggleItemGroupIsCheck(false);
                }
            } else {
                dispatch.lists.setActiveGroupKey(null);
                dispatch.tablets.toggleItemGroupIsCheck(false);
            }
            dispatch.tree.setCalculationObject(null);
        },

        async fetchWellsInfo(payload) {
            try {
                const body = {
                    well_ids: payload,
                };
                const response = await apiv2.tree.fetchWellInfoByIds(body);
                return response.data;
            } catch (error) {
                console.error('fetchWellsInfo', error);
                return [];
            }
        },

        async addItem(payload, rootState) {
            const { data } = rootState.lists;
            const currentData = data || [];
            const key = sha256(`item${Math.random()}`).toString();
            const newItem = { ...payload, key };
            const item = itemInLists(payload);
            if (item) {
                dispatch.lists.setError('Данная скважина уже есть в списках');
                return item.key;
            } else {
                const updatedItem = await dispatch.lists.addCustomMetadata(newItem);
                const info = await dispatch.lists.fetchWellsInfo([newItem.id]);

                const infoObj = info.reduce((acc, item) => {
                    const treeItem = findObjectById(item.id, rootState.tree.data);
                    return {
                        fieldId: item.fieldId,
                        fieldTitle: item.fieldTitle,
                        ventureTitle: item.ventureTitle,
                        parents: treeItem.parents,
                    };
                }, {});
                // For tests
                // const updatedItem = newItem;
                const newData = [...currentData, { ...updatedItem, ...infoObj }];

                await dispatch.lists.saveData(newData);
                dispatch.lists.setDataWithDisplaySettings(newData);
            }

            return key;
        },

        async addItems(payload, rootState) {
            const { data } = rootState.lists;
            const currentData = data || [];

            const newItems = payload.map(e => {
                return { ...e, key: sha256(`item${Math.random()}`).toString() };
            });

            const wellInformation = await dispatch.lists.fetchWellsInfo(newItems.map(item => item.id));

            const listToAdd = [];
            for (var i = 0; i < newItems.length; i++) {
                const newItem = newItems[i];
                const index = currentData.findIndex(item => item?.id === newItem.id);
                if (index === -1) {
                    const updatedItem = await dispatch.lists.addCustomMetadata(newItem);
                    const itemInfo = wellInformation.find(item => item.id === updatedItem.id) || {};
                    listToAdd.push({ ...updatedItem, ...itemInfo });
                }
            }
            const newData = [...currentData, ...listToAdd];

            await dispatch.lists.saveData(newData);
            dispatch.lists.setDataWithDisplaySettings(newData);
        },

        async addWellToLists(payload, rootState) {
            const { data } = rootState.lists;
            const currentData = data || [];
            if (!itemInLists(payload)) {
                const newItem = { ...payload, key: sha256(`item${Math.random()}`).toString() };
                const wellInformation = await dispatch.lists.fetchWellsInfo([newItem.id]);

                const updatedItem = await dispatch.lists.addCustomMetadata(newItem);
                const itemInfo = wellInformation.find(item => item.id === updatedItem.id) || {};

                const newData = [...currentData, { ...updatedItem, ...itemInfo }];

                await dispatch.lists.saveData(newData);
                dispatch.lists.setDataWithDisplaySettings(newData);
                dispatch.lists.selectItem(newItem.key);
            } else {
                dispatch.lists.selectItem(payload.key);
            }
        },

        async openNewGroupByItems(payload, rootState) {
            const { data, groupTitle = null, id, type, modTitle } = payload;

            const newData = rootState.lists.data === null ? [] : [...rootState.lists.data];

            if (data.length > 0) {
                const newItems = data.map(e => {
                    return { ...e, key: sha256(`item${Math.random()}`).toString() };
                });
                const wellInformation = await dispatch.lists.fetchWellsInfo(newItems.map(item => item.id));
                const listToAdd = [];
                for (var i = 0; i < newItems.length; i++) {
                    const newItem = newItems[i];
                    const updatedItem = await dispatch.lists.addCustomMetadata(newItem);
                    const itemInfo = wellInformation.find(item => item.id === updatedItem.id) || {};

                    listToAdd.push({ ...updatedItem, ...itemInfo });
                }

                const newGroup = {
                    id,
                    type,
                    title: groupTitle ?? data[0].fieldTitle,
                    key: sha256(`group${Math.random()}`).toString(),
                    node: NODE_TYPE.GROUP,
                    children: listToAdd,
                };

                const preparedNewData = prepareDataRecursive([...newData, newGroup]);
                dispatch.lists.setDataWithDisplaySettings(preparedNewData);
                dispatch.lists.saveData(preparedNewData);
                if (
                    (type === 'shaft' && modTitle !== 'Добыча/закачка') ||
                    type === 'well' ||
                    type === 'pad' ||
                    type === 'wellStation' ||
                    type === 'group'
                ) {
                    dispatch.lists.setActiveGroupKey(newGroup.key);
                    dispatch.lists.selectItem(newGroup.key);
                    dispatch.lists.renameItem(newGroup.key);
                }
            }
        },
        async updateGroupByItems(payload, rootState) {
            const { groupKey, items } = payload;
            // const activeGroup = getItemByKeyRecursive(rootState.lists.data, groupKey);
            const groupIndex = rootState.lists.data.findIndex(el => el.key === groupKey);
            if (groupIndex >= 0 && items.length > 0) {
                const newItems = items.map(e => {
                    return { ...e, key: sha256(`item${Math.random()}`).toString() };
                });

                const listToAdd = [];
                for (var i = 0; i < newItems.length; i++) {
                    const newItem = newItems[i];
                    const updatedItem = await dispatch.lists.addCustomMetadata(newItem);
                    listToAdd.push(updatedItem);
                }

                const group = { ...rootState.lists.data[groupIndex], children: listToAdd };

                const newData = [...rootState.lists.data];
                newData[groupIndex] = group;

                const preparedNewData = prepareDataRecursive(newData);
                dispatch.lists.setDataWithDisplaySettings(preparedNewData);
                dispatch.lists.saveData(preparedNewData);
            }
        },

        async addItemInGroup(payload, rootState) {
            const { activeItemKey, activeGroupKey } = rootState.lists;
            const newData = [...rootState.lists.data];
            const newItem = { ...payload, key: sha256(`item${Math.random()}`).toString() };

            const info = await dispatch.lists.fetchWellsInfo([newItem.id]);

            const infoObj = info.reduce((acc, item) => {
                const treeItem = findObjectById(item.id, rootState.tree.data);
                return {
                    fieldId: item.fieldId,
                    fieldTitle: item.fieldTitle,
                    ventureTitle: item.ventureTitle,
                    parents: treeItem.parents,
                };
            }, {});
            const updatedItem = await dispatch.lists.addCustomMetadata(newItem);
            const itemWithInfo = { ...updatedItem, ...infoObj };
            if (activeGroupKey) {
                const activeItem = getItemByKeyRecursive(newData, activeGroupKey);
                if (activeItem?.children) {
                    const index = activeItem.children.findIndex(item => item?.id === itemWithInfo.id);
                    if (index === -1) {
                        const newActiveItem = { ...activeItem };
                        newActiveItem.children.push({ ...itemWithInfo, ...infoObj });
                        const updatedNewData = setItemByKeyRecursive(newData, activeGroupKey, newActiveItem);
                        dispatch.lists.setDataWithDisplaySettings(updatedNewData);
                        await dispatch.lists.saveData(updatedNewData);
                        dispatch.lists.selectItem(activeGroupKey);
                    } else {
                        dispatch.lists.setError('Данная скважина уже есть в этой группе');
                    }
                } else {
                    const newActiveItem = { ...activeItem, children: [{ ...itemWithInfo, ...infoObj }] };
                    const updatedNewData = setItemByKeyRecursive(newData, activeGroupKey, newActiveItem);
                    dispatch.lists.setDataWithDisplaySettings(updatedNewData);
                    await dispatch.lists.saveData(updatedNewData);
                    dispatch.lists.selectItem(activeGroupKey);
                }
            } else if (activeItemKey) {
                const activeItem = getItemByKeyRecursive(newData, activeItemKey);
                if (activeItem.node !== NODE_TYPE.GROUP) {
                    if (activeItem.id !== itemWithInfo.id) {
                        const otherItem = { ...activeItem };
                        const newGroup = {
                            title: otherItem.title,
                            key: sha256(`group${Math.random()}`).toString(),
                            node: NODE_TYPE.GROUP,
                            children: [otherItem, itemWithInfo],
                        };
                        const updatedNewData = setItemByKeyRecursive(newData, activeItemKey, newGroup);
                        dispatch.lists.setDataWithDisplaySettings(updatedNewData);
                        await dispatch.lists.saveData(updatedNewData);
                        dispatch.lists.selectItem(activeItemKey);
                    } else {
                        dispatch.lists.setError('Нельзя создать группу из двух одинаковых скважин');
                    }
                } else if (activeItem.node === NODE_TYPE.GROUP) {
                    if (activeItem.children) {
                        const index = activeItem.children.findIndex(item => item.id === itemWithInfo.id);
                        if (index === -1) {
                            const newActiveItem = { ...activeItem };
                            newActiveItem.children.push(itemWithInfo);
                            const updatedNewData = setItemByKeyRecursive(newData, activeItemKey, newActiveItem);
                            dispatch.lists.setDataWithDisplaySettings(updatedNewData);
                            await dispatch.lists.saveData(updatedNewData);
                            dispatch.lists.selectItem(activeItemKey);
                        } else {
                            dispatch.lists.setError('Данная скважина уже есть в этой группе');
                        }
                    } else {
                        const newActiveItem = { ...activeItem, children: [itemWithInfo] };
                        const updatedNewData = setItemByKeyRecursive(newData, activeItemKey, newActiveItem);
                        dispatch.lists.setDataWithDisplaySettings(updatedNewData);
                        await dispatch.lists.saveData(updatedNewData);
                        dispatch.lists.selectItem(activeItemKey);
                    }
                }
            }
        },
        async removeItem(payload, rootState) {
            const key = payload;

            dispatch.tablets.removeTablet(payload);

            const { data, activeItemKey, activeGroupKey } = rootState.lists;
            const { calculationObject } = rootState.tree;

            const removedNewDate = removeItemByKeyRecursive(data, key);
            const preparedNewData = prepareDataRecursive(removedNewDate);

            if (preparedNewData.length !== 0) {
                await dispatch.lists.saveData(preparedNewData);

                dispatch.lists.setData(preparedNewData);
            } else {
                await dispatch.lists.saveData(null);
                dispatch.lists.setDataWithDisplaySettings(null);
            }

            if (activeItemKey === key || activeGroupKey === key) {
                dispatch.lists.selectItem(null);
            }
            if (!activeItemKey && !activeGroupKey && calculationObject?.node === 'shaft') {
                dispatch.lists.selectItem(null);
            }
        },
        async clearAllList() {
            await dispatch.lists.saveData(null);
            await dispatch.lists.setData(null);
            await dispatch.lists.setActiveItemKey(null);
        },
        renameItem(payload, rootState) {
            const key = payload;
            const newData = [...rootState.lists.data];
            const item = getItemByKeyRecursive(newData, key);
            if (item) {
                const newItem = {
                    ...item,
                    isRenaming: true,
                };
                const updatedNewData = setItemByKeyRecursive(newData, key, newItem);
                dispatch.lists.setDataWithDisplaySettings(updatedNewData);
                dispatch.lists.selectItem(key);
            }
        },
        setCorrelation(payload, rootState) {
            const { listKey, isCorrelation } = payload;
            const newData = [...rootState.lists.data];
            const item = getItemByKeyRecursive(newData, listKey);
            if (item?.isCorrelation === isCorrelation) return;
            if (item) {
                const newItem = {
                    ...item,
                    isCorrelation,
                };
                const updatedNewData = setItemByKeyRecursive(newData, listKey, newItem);
                dispatch.lists.setDataWithDisplaySettings(updatedNewData);
                dispatch.lists.saveData(updatedNewData);
            }
        },
        saveTitleItem(payload, rootState) {
            const { key, title } = payload;
            const newData = [...rootState.lists.data];
            const item = getItemByKeyRecursive(newData, key);
            if (item) {
                const newItem = {
                    ...item,
                    title,
                    isRenaming: false,
                };
                const updatedNewData = setItemByKeyRecursive(newData, key, newItem);
                dispatch.lists.setDataWithDisplaySettings(updatedNewData);
                dispatch.lists.saveData(updatedNewData);
                dispatch.lists.selectItem(key);
            }
        },
        duplicateItem(payload, rootState) {
            const key = payload;
            const newData = [...rootState.lists.data];
            const item = getItemByKeyRecursive(newData, key);
            if (item) {
                const newItem = {
                    ...item,
                    title: `${item.title} (Копия)`,
                    key: sha256(`${item.key}${Math.random()}`).toString(),
                    children: setUniqueKeyToItemsRecursive(item.children),
                };
                newData.push(newItem);
                dispatch.lists.setDataWithDisplaySettings(newData);
                dispatch.lists.saveData(newData);
                dispatch.lists.selectItem(newItem.key);
            }
        },
        openNewGroupByItem(payload, rootState) {
            const key = payload;
            const newData = [...rootState.lists.data];
            const item = getItemByKeyRecursive(newData, key);
            const treeItem = findObjectById(item.id, rootState.tree.data);

            if (treeItem && !item.parents) {
                item.parents = treeItem.parents;
            }
            if (item) {
                const childItem = { ...item };
                const newGroup = {
                    title: childItem?.fieldTitle ?? childItem.title,
                    key: sha256(`group${Math.random()}`).toString(),
                    node: NODE_TYPE.GROUP,
                    children: [childItem],
                    parents: item.parents,
                };
                const removedNewData = removeItemByKeyRecursive(newData, key);
                removedNewData.push(newGroup);
                const preparedNewData = prepareDataRecursive(removedNewData);
                dispatch.lists.setDataWithDisplaySettings(preparedNewData);
                dispatch.lists.saveData(preparedNewData);
                dispatch.lists.renameItem(newGroup.key);
                dispatch.lists.selectItem(newGroup.key);
            }
        },
        // eslint-disable-next-line no-unused-vars
        setGroupActiveModule(payload, rootState) {
            // TODO: уточнить актуальность метода
            //
            // const activeModuleId = payload;
            // const newData = [...rootState.lists.data];
            // const { activeItemKey, activeGroupKey } = rootState.lists;
            // if (activeGroupKey) {
            //     const activeGroup = getItemByKeyRecursive(newData, activeGroupKey);
            //     if (activeGroup) {
            //         const newGroup = { ...activeGroup, activeModuleId };
            //         const updatedNewData = setItemByKeyRecursive(newData, activeGroupKey, newGroup);
            //         dispatch.lists.setData(updatedNewData);
            //         dispatch.lists.saveData(updatedNewData);
            //     }
            // } else {
            //     const activeGroup = getItemByKeyRecursive(newData, activeItemKey);
            //     if (activeGroup?.node === NODE_TYPE.GROUP) {
            //         const newGroup = { ...activeGroup, activeModuleId };
            //         const updatedNewData = setItemByKeyRecursive(newData, activeItemKey, newGroup);
            //         dispatch.lists.setData(updatedNewData);
            //         dispatch.lists.saveData(updatedNewData);
            //     }
            // }
        },
        setDataWithDisplaySettings(payload, rootState) {
            // const { emptyColumnsDataSettings } = rootState.displaySettings;
            const currentData = rootState.lists.data || [];

            // Преобразуем текущий lists.data в маппинг для быстрого доступа (работа с Map имеет временную сложность O(1) для поиска, что быстрее, чем линейный поиск по массиву)
            const currentDataMap = new Map(currentData.map(item => [item.id, item]));

            // Фильтруем только новые cкважины
            const updatedData = payload?.filter(newItem => {
                const currentItem = currentDataMap.get(newItem.id);
                return (
                    !currentItem ||
                    (JSON.stringify(currentItem) !== JSON.stringify(newItem) && newItem.nodeType === 'well')
                );
            });

            dispatch.lists.setData(payload);
        },
    }),
    selectors: (slice, createSelector) => ({
        groupActiveModule() {
            return slice(lists => {
                if (lists.activeGroupKey) {
                    const group = getItemByKeyRecursive(lists.data, lists.activeGroupKey);
                    return group?.activeModuleId || null;
                }
                if (lists.activeItemKey) {
                    const item = getItemByKeyRecursive(lists.data, lists.activeItemKey);
                    return item?.node === NODE_TYPE.GROUP ? item?.activeModuleId || null : null;
                }
                return null;
            });
        },
        activeItem() {
            return slice(lists => {
                if (lists.activeItemKey) {
                    const item = getItemByKeyRecursive(lists.data, lists.activeItemKey);
                    return item?.node !== NODE_TYPE.GROUP ? item : null;
                }
                return null;
            });
        },
        selectedItem() {
            return slice(lists => {
                if (lists.activeItemKey) {
                    const item = getItemByKeyRecursive(lists.data, lists.activeItemKey);
                    return item;
                }

                return null;
            });
        },
        activeGroup() {
            return slice(lists => {
                if (lists.activeGroupKey) {
                    const item = getItemByKeyRecursive(lists.data, lists.activeGroupKey);
                    return item?.node === NODE_TYPE.GROUP ? item : null;
                }
                if (lists.activeItemKey) {
                    const item = getItemByKeyRecursive(lists.data, lists.activeItemKey);
                    return item?.node === NODE_TYPE.GROUP ? item : null;
                }
                return null;
            });
        },
        wellsByGroupKey() {
            return createSelector(
                rootState => rootState.lists.data,
                rootState => rootState.lists.groupKeyForReorder,
                (data, key) => {
                    const group = data.find(item => {
                        return item.node === 'group' && item.key === key;
                    });

                    // return group ? group.children.map(well => ({ id: well.id, title: well.title })) : null;
                    return group ? group.children : null;
                },
            );
        },
    }),
};

export default listsModel;
